import { useState, useEffect, useRef, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { QuantityModal } from './quantity-modal';
import { AutoComplete, type Option } from "@/components/styled-autocomplete"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { Label } from "@/components/ui/label"
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"


import Footer from './footer'

import { CheckCircle, DeleteIcon } from 'lucide-react'
import { InventoryCombinedData, Offer } from '../-data/models/inventoryData'
import { convertToCurrencyRounded, getDiscount, getItemCost } from '@/utils/currency_converter'
import { toast } from '@/components/ui/use-toast'
import { useDebounce } from "@uidotdev/usehooks";
import dayjs from 'dayjs'
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import StartShiftModal from '@/components/shifts/start-shift-modal'
import NoShiftFound from './no-shift-found'
import { Route as salesRoute } from '@/routes/(pharma)/_layout/sales/index'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { useHotkeys } from 'react-hotkeys-hook';
import NoSaleSelected from './no-sale-selected'
import { DiscountModal } from './discount-modal';
import db from '@/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { useNavigate } from '@tanstack/react-router';
import { tableHeadClass, tableCellClass } from '@/components/ui/table-styles';
import { cn } from "@/lib/utils";
import { useMutation } from '@connectrpc/connect-query';
import { searchInventory } from '@/gen/pharmacy-PharmacyService_connectquery';
import { SearchInventoryRequest } from '@/gen/search/search_inventory_pb';
import { DiscountType, ItemType } from '@/gen/inventory/common_pb';
import { pharmacyClient } from '@/connect';
import { GS1Reader } from 'gs1js';

const component = () => {
    const navigate = useNavigate();
    const data = salesRoute.useLoaderData();
    const [search, setSearch] = useState('')
    const [results, setResults] = useState<Option[]>([])
    const [selected, setSelected] = useState<InventoryCombinedData[]>([])
    const [switchInsurance, setSwitchInsurance] = useState(false)
    const [emiratesID, setEmiratesID] = useState('')
    const [value, setValue] = useState<Option | undefined>(undefined)
    const [openShiftStart, setOpenShiftStart] = useState(!data.activeShiftFound) // Needs to be checked from backend @nanduri1998
    const [displaySalesComp, setDisplaySalesComp] = useState(data.activeShiftFound) // Needs to be checked from backend @nanduri1998
    const [resetTrigger, setResetTrigger] = useState<number>(0);
    const debouncedSearchTerm = useDebounce(search, 300);
    const autoCompleteRef = useRef<HTMLInputElement>(null);
    const checkoutButtonRef = useRef<HTMLButtonElement>(null);

    const [selectedItemIndex, setSelectedItemIndex] = useState(-1);

    const [stockPointId, setStockPointId] = useState<string>('');

    const active_sales = useLiveQuery(() => db.active_sales.toArray());
    const selected_sale = useLiveQuery(() => db.active_sales.filter(a => a.selected).first());
    const selected_items = useLiveQuery(() => db.sales_items.toArray());
    const selected_items_batches = useLiveQuery(() => db.sales_items_batches.toArray());
    const [barcodeInput, setBarcodeInput] = useState('');
    const [isProcessingBarcode, setIsProcessingBarcode] = useState(false);
    const barcodeInputRef = useRef<HTMLInputElement>(null);
    const BARCODE_TIMEOUT = 100;


    useEffect(() => {
        const storedStockPointId = localStorage.getItem('stockPointId');
        if (storedStockPointId) {
            setStockPointId(storedStockPointId);
        }
    }, []);

    useHotkeys('up', (event) => {
        event.preventDefault();
        setSelectedItemIndex((prevIndex) => {
            const newIndex = prevIndex > 0 ? prevIndex - 1 : 0;
            return newIndex;
        });
    });

    useHotkeys('down', (event) => {
        event.preventDefault();
        setSelectedItemIndex((prevIndex) => {
            const maxIndex = (selected_items?.filter(a => a.sale_id === selected_sale?.id).length || 0) - 1;
            const newIndex = prevIndex < maxIndex ? prevIndex + 1 : maxIndex;
            return newIndex;
        });
    });

    useHotkeys('s', (event) => {
        event.preventDefault();
        // Cycle between active_sales
        if (active_sales && active_sales.length === 1) return;
        const currentIndex = selected_sale && active_sales?.findIndex(a => a.id === selected_sale?.id);
        if (currentIndex === active_sales?.length! - 1) {
            (selected_sale && active_sales) && db.active_sales.update(selected_sale?.id, { selected: true });
        } else {
            (selected_sale && active_sales) && db.active_sales.update(selected_sale?.id, { selected: false });
        }
    });

    useHotkeys('space', (event) => {
        event.preventDefault();
        autoCompleteRef.current?.focus();
    });

    useHotkeys('shift+enter', (event) => {
        event.preventDefault();
        if (checkoutButtonRef.current) {
            checkoutButtonRef.current.click();
        } else {
            console.log('Checkout button ref is null');
        }
    });

    useHotkeys('shift+s', (event) => {
        event.preventDefault();
        addNewMultiSale();
    });

    useHotkeys('shift+d', (event) => {
        event.preventDefault();
        if (active_sales?.length === 1) return;
        try {
            db.transaction('rw', db.active_sales, db.sales_items, db.sales_items_batches, async (tx) => {
                const to_beDeleted_sale = await tx.active_sales.where('id').equals(selected_sale?.id!).toArray();
                const to_beDeletedSaleItems = await tx.sales_items.where('sale_id').equals(selected_sale?.id!).toArray();
                const to_beDeletedSaleItemsBatches = await tx.sales_items_batches.where('sale_id').equals(selected_sale?.id!).toArray();
                await tx.active_sales.bulkDelete(to_beDeleted_sale.map(a => a.id));
                await tx.sales_items.bulkDelete(to_beDeletedSaleItems.map(a => a.id));
                await tx.sales_items_batches.bulkDelete(to_beDeletedSaleItemsBatches.map(a => a.id));
                active_sales && (await tx.active_sales.update(active_sales[0].id, { selected: true }));
            })
        } catch (e) {
            console.error(e)
        }
    });

    useHotkeys('+,right', (event) => {
        event.preventDefault();
        if (selectedItemIndex >= 0 && selected_items) {
            const selectedItem = selected_items.filter(a => a.sale_id === selected_sale?.id)[selectedItemIndex];
            if (selectedItem) {
                try {
                    db.transaction('rw', db.active_sales, db.sales_items, db.sales_items_batches, async (tx) => {
                        const quantity = await tx.sales_items.where('sale_id').equals(selected_sale?.id!).and(item => item.item_id === selectedItem.item_id).toArray();
                        const availableStock = selected_items_batches?.find((batch) => batch.item_id === selectedItem.item_id && batch.is_selected && batch.sale_id === selectedItem.sale_id)?.available_stock!
                        if (quantity[0].quantity_added === availableStock) {
                            toast({
                                title: "Error",
                                variant: "destructive",
                                description: "Cannot add more than available stock",
                                duration: 5000,
                            });
                            return;
                        }
                        await tx.sales_items.update(quantity[0].id, { quantity_added: quantity[0].quantity_added + 1 });
                    })
                } catch (e) {
                    console.error(e)
                }
            }
        }
    });

    useHotkeys('-,left', (event) => {
        event.preventDefault();
        if (selectedItemIndex >= 0 && selected_items) {
            const selectedItem = selected_items.filter(a => a.sale_id === selected_sale?.id)[selectedItemIndex];
            if (selectedItem) {
                try {
                    db.transaction('rw', db.active_sales, db.sales_items, db.sales_items_batches, async (tx) => {
                        const quantity = await tx.sales_items.where('sale_id').equals(selected_sale?.id!).and(item => item.item_id === selectedItem.item_id).toArray();
                        if (quantity[0].quantity_added === 1) {
                            toast({
                                title: "Error",
                                variant: "destructive",
                                description: "Cannot reduce quantity less than 1",
                                duration: 5000,
                            });
                            return;
                        }
                        await tx.sales_items.update(quantity[0].id, { quantity_added: quantity[0].quantity_added - 1 });
                    })
                } catch (e) {
                    console.error(e)
                }
            }
        }
    });

    useHotkeys('shift+backspace', (event) => {
        event.preventDefault();
        if (selectedItemIndex >= 0 && selected_items) {
            const selectedItem = selected_items.filter(a => a.sale_id === selected_sale?.id)[selectedItemIndex];
            if (selectedItem) {
                try {
                    db.transaction('rw', db.active_sales, db.sales_items, db.sales_items_batches, async (tx) => {
                        const quantity = await tx.sales_items.where('sale_id').equals(selected_sale?.id!).and(item => item.item_id === selectedItem.item_id).toArray();
                        await tx.sales_items.delete(quantity[0].id);
                        const batch = await tx.sales_items_batches.where('sale_id').equals(selected_sale?.id!).and(item => item.item_id === selectedItem.item_id).toArray();
                        await tx.sales_items_batches.delete(batch[0].id);
                    })
                } catch (e) {
                    console.error(e)
                }
            }
        }
    });

      useEffect(() => {
        interface KeystrokeData {
          key: string;
          code: string;
          timestamp: number;
          altKey?: boolean;
          ctrlKey?: boolean;
          shiftKey?: boolean;
        }
      
        let keystrokeBuffer: KeystrokeData[] = [];
        let scanTimer: ReturnType<typeof setTimeout> | null = null;
        let isScanning = false;
      
        const captureRawKeystroke = (event: KeyboardEvent) => {
          
          const now = Date.now();
          const timeSinceLastKey = keystrokeBuffer.length > 0 ? 
            now - keystrokeBuffer[keystrokeBuffer.length - 1].timestamp : 0;
          
    
          // Start scanning on rapid keystrokes and continue scanning if already scanning
          if ((timeSinceLastKey < 50 && timeSinceLastKey > 0) || isScanning ){
            isScanning = true;
          }
      
          if (keystrokeBuffer.length === 0 && event.key.length === 1) {
            isScanning = true;
          }
      
          if (isScanning) {
            keystrokeBuffer.push({
              key: event.key,
              code: event.code,
              timestamp: now,
              altKey: event.altKey,
              ctrlKey: event.ctrlKey,
              shiftKey: event.shiftKey
            });
            
            // Only preventDefault on scanner-specific keystrokes
            const isScannerKeystroke = (
              // Function keys used as separators
              event.key === 'F1' || event.key === 'F7' || event.key === 'F8' || 
              event.key === 'F11' || event.key === 'F12' ||
              // Control combinations
              (event.ctrlKey && (event.key === ']' || event.key === '+')) ||
              // Separator substitutes
              event.key === '~' || event.key === '|' ||
              // Other scanner-specific keys
              event.code === 'Oem6'
            );
            
            if (isScannerKeystroke) {
              event.preventDefault();
              event.stopPropagation();
            }
            
            if (scanTimer) clearTimeout(scanTimer);
    
    
            const isSpecialKey = event.key === 'Alt' || event.key === '≠' || event.altKey || event.shiftKey;
            const timeoutDelay = isSpecialKey ? 500 : 150; // Longer timeout for special keys
    
            scanTimer = setTimeout(() => {
              const reconstructedString = reconstructString(keystrokeBuffer);
              
              // Test the parsing
              testParser(reconstructedString);
              
              // Try actual form update
              if (reconstructedString.length > 10) {
                extractGTINAndSearch(reconstructedString);
              }
              
              // Reset
              keystrokeBuffer = [];
              isScanning = false;
            }, timeoutDelay);
          }
        };
      
        const reconstructString = (keystrokes: KeystrokeData[]): string => {
          let result = '';
          let i = 0;
          
          while (i < keystrokes.length) {
            const k = keystrokes[i];
            
            // Handle GS1 separator keystrokes
            if (
              k.key === 'F1' || k.key === 'F7' || k.key === 'F8' || 
              k.key === 'F11' || k.key === 'F12'
            ) {
              result += '\u001D'; // Convert to GS1 separator
            }
            // Alt+0029 sequence detection
            else if (k.altKey && k.key === '0' && i < keystrokes.length - 3) {
              
              const next1 = keystrokes[i + 1];
              const next2 = keystrokes[i + 2]; 
              const next3 = keystrokes[i + 3];
              
              
              // Check for Alt+0+0+2+9 or Alt+0+2+9 patterns
              if (next1?.key === '0' && next2?.key === '2' && next3?.key === '9') {
                result += '\u001D';
                i += 3; // Skip the 0,2,9 keys
              }
              else if (i < keystrokes.length - 4 && next1?.key === '0' && next2?.key === '0' && next3?.key === '2' && keystrokes[i + 4]?.key === '9') {
                result += '\u001D';
                i += 4; // Skip the 0,0,2,9 keys
              }
              else {
                result += '0';
              }
            }
            else if (k.key === 'Alt' || k.key === '≠' || (k.altKey && k.key !== '0')) {
              
              // Look for Alt+0029 pattern
              if (i < keystrokes.length - 3) {
                const next1 = keystrokes[i + 1];
                const next2 = keystrokes[i + 2]; 
                const next3 = keystrokes[i + 3];
                
                
                if (next1?.key === '0' && next2?.key === '2' && next3?.key === '9') {
                  result += '\u001D';
                  i += 3; // Skip the 0, 2, 9
                } else {
                }
              } else {
              }
            }
            // Ctrl key combinations
            else if (i < keystrokes.length - 1) {
              const nextKey = keystrokes[i + 1];
              
              if ((k.key === 'Control' && nextKey?.key === ']') || 
                  (k.key === ']' && keystrokes.some(ks => ks.key === 'Control'))) {
                result += '\u001D';
                if (k.key === 'Control') i++;
              }
              else if ((k.key === 'Control' && nextKey?.key === '+') || 
                       (k.key === '+' && keystrokes.some(ks => ks.key === 'Control'))) {
                result += '\u001D';
                if (k.key === 'Control') i++;
              }
              else if (k.key === 'Control') {
              } else {
                if (k.key.length === 1 && /^[a-zA-Z0-9]$/.test(k.key)) {
                  result += k.key;
                }
              }
            }
            // Printable substitutes
            else if (k.key === '~' || k.key === '|') {
              result += '\u001D';
            }
            // Datalogic Oem6 key
            else if (k.code === 'Oem6' || k.key === 'Oem6') {
              result += '\u001D';
            }
            // Regular alphanumeric characters
            else if (k.key.length === 1 && /^[a-zA-Z0-9]$/.test(k.key)) {
              result += k.key;
            }
            // Handle Shift+Letter combinations
            else if (k.shiftKey && k.key.length === 1 && /^[A-Z]$/.test(k.key)) {
              result += k.key;
            }
            i++;
          }
          
          return result.replace(/Enter$/, '');
        };
      
        const testParser = (dataMatrix: string) => {
          
          const result: Record<string, string> = {};
          const gs = new GS1Reader(dataMatrix);
          
          if (gs.lookup["01"]) {
            const gtin = gs.lookup["01"].value;
            result['01'] = gtin;
            
            if(gs.lookup["17"]) {
              const expiry = gs.lookup["17"].value;
              result['17'] = expiry;
            }
      
            if(gs.lookup["10"]) {
              const batch = gs.lookup["10"].value;
              result['10'] = batch;
            }
      
            if(gs.lookup["21"]) {
              const serial = gs.lookup["21"].value;
              result['21'] = serial;
            }
      
          } else {
            console.log('No GTIN found in GS1 data');
          }
          
          return result;
        };
      
        const extractGTINAndSearch = (barcodeData: string) => {
            try {
                // Parse GS1 barcode
                const parsedData = testParser(barcodeData);
                
                // Extract GTIN (AI 01)
                
                if (parsedData['01']) {
                    console.log('Scanned GTIN:', parsedData['01']);
                    // Set the search field with the GTIN
                    setSearch(parsedData['01']);
                    
                } else {
                    console.warn('No GTIN found in barcode');
                    toast({
                        title: "Invalid Barcode",
                        description: "Could not find product identifier (GTIN) in barcode",
                        variant: "destructive",
                        duration: 2000,
                    });
                    
                }
            } catch (error) {
                console.error('Error parsing GS1 barcode:', error);

            }
        };
      
        // Add event listener
        document.addEventListener('keydown', captureRawKeystroke, true);
        
        return () => {
          document.removeEventListener('keydown', captureRawKeystroke, true);
          if (scanTimer) clearTimeout(scanTimer);
        };
      }, []);


    const addNewMultiSale = async () => {
        await db.transaction('rw', db.active_sales, async (tx) => {
            const activeNumberOfSales = await db.active_sales.count();
            if (activeNumberOfSales >= 3) {
                toast({
                    title: "Error",
                    variant: "destructive",
                    description: "Cannot add more than 3 sales",
                    duration: 5000,
                });
                return;
            }
            if (active_sales?.length === 0) {
                await tx.active_sales.add({ created_at: new Date(), selected: true });
                return;
            }
            await tx.active_sales.add({ created_at: new Date(), selected: false });
        })
    }

    const { netTotal, totaldiscount, totaltax, applicableTaxRates } = useMemo(() => {
        let netTotal = 0;
        let totaldiscount = 0;
        let totaltax = 0;
        let applicableTaxRates: { name: string | undefined; rate: number | undefined; amount: number; }[] = [];
        if (!selected_items) {
            return {
                netTotal, totaldiscount, totaltax, applicableTaxRates
            }
        }
        if (!selected_items_batches) {
            return {
                netTotal, totaldiscount, totaltax, applicableTaxRates
            }
        }
        for (let item of selected_items.filter(a => a.sale_id === selected_sale?.id)) {
            const price = selected_items_batches?.find((batch) => batch.item_id === item.item_id && batch.is_selected && batch.sale_id === item.sale_id)?.price!;
            const discount_type = selected_items_batches?.find((batch) => batch.item_id === item.item_id && batch.is_selected && batch.sale_id === item.sale_id)?.max_discount_type;
            const tax_profile = selected_items_batches?.find((batch) => batch.item_id === item.item_id && batch.is_selected && batch.sale_id === item.sale_id)?.tax_profile;
            const item_cost = getItemCost({
                price: price,
                quantity: item.quantity_added!,
                taxProfile: tax_profile,
                discount: item.item_discount!,
                discount_type: discount_type!
            })
            netTotal = netTotal + item_cost.cost;
            const item_discount = getDiscount({
                price: price,
                quantity: item.quantity_added!,
                discount_amt: item.item_discount!,
                discount_type: discount_type!
            })
            totaldiscount = totaldiscount + item_discount;
            const itemTax = item_cost.totalTax;
            totaltax = totaltax + itemTax;
            applicableTaxRates = applicableTaxRates.concat(item_cost.applicableTaxesRates!);
        }
        return {
            netTotal,
            totaldiscount,
            totaltax,
            applicableTaxRates: applicableTaxRates
        }
    }, [selected_items, selected_sale, selected_items_batches])

    const searchMutation = useMutation(searchInventory, {
        onSuccess: (response) => {
            setResults(response.items.map((item) => {
                    const name = item.tradeName + (item.barcode ? " | " + item.barcode : "") + " | " + item.unitType + ":::" + item.manufacturer + ":::" + item.owner
                    return {
                        value: item.id,
                        label: name,
                        type: item.type.toString(),
                        barcode: item.barcode,
                        trade_name: item.tradeName,
                        unit: item.unit.toString(),
                        unit_type: item.unitType,
                        manufacturer: item.manufacturer,
                        owner: item.owner,
                        is_suggestion: item.isSuggestion ? 'yes' : 'no',
                        ranking_score: item.rankingScore.toString(),
                        available_stock: item.availableStock.toString(),
                        is_add: "no",
                        average_price: item.averagePrice ? item.averagePrice.toString() : "0",
                        active_batch_count: item.activeBatchCount.toString(),
                        scientific_name: item.scientificName,
                    }
                }))
        },
        onError: (error) => {
            console.error('Error:', error);
            setResults([])
            toast({
                title: "Error",
                variant: "destructive",
                description: "Failed to search",
                duration: 5000,
            });
        }
    })

    useEffect(() => {
        if (data.activeShiftFound && debouncedSearchTerm && stockPointId) {
            const searchData = new SearchInventoryRequest();
            searchData.onlyAvailable = true;
            searchData.limit = 10;
            searchData.query = debouncedSearchTerm;
            searchData.stockPointId = stockPointId;
            searchMutation.mutate(searchData);
        } else if (!debouncedSearchTerm) {
            // Clear results when search is empty
            setResults([]);
        }
    }, [debouncedSearchTerm, stockPointId, data.activeShiftFound]);

    useEffect(() => {
        if (data) {
            if (data.last_shift_system_ended) {
                navigate({
                    to: '/reports/shift-history/shift-summary/$shiftId',
                    params: { shiftId: data.last_shift_id }
                });
            }
            setOpenShiftStart(!data.activeShiftFound)
            setDisplaySalesComp(data.activeShiftFound)
        }
    }, [data])

    useEffect(() => {
        setValue(undefined);
        setSearch('');
        setResults([]);
        setResetTrigger(prev => prev + 1);
    }, [selected_sale?.id]);

    useEffect(() => {
        if (!value) return;
        
        const selectedValue = value;
        
        setValue(undefined);
        setSearch('');
        setResults([]);
        
        const getMed = async () => {
            try {
                const item = await pharmacyClient.getInventory({
                    page: 1,
                    pageSize: 1000,
                    filter: {
                        masterId: selectedValue.value,
                        isAvailable: true,
                        itemType: selectedValue.type === ItemType.MEDICAL.toString() ? ItemType.MEDICAL : ItemType.NON_MEDICAL,
                    }
                });
                
                await db.transaction('rw', db.sales_items, db.sales_items_batches, async (tx) => {
                    let item_discount = 0;
                    if (item.items[0].metadata?.discountType === DiscountType.ALWAYS_FIXED || item.items[0].metadata?.discountType === DiscountType.ALWAYS_PERCENTAGE) {
                        if (item.items[0].metadata?.discountType === DiscountType.ALWAYS_FIXED) {
                            item_discount = item.items[0].metadata?.discountValue!;
                        } else if (item.items[0].metadata?.discountType === DiscountType.ALWAYS_PERCENTAGE) {
                            item_discount = item.items[0].metadata.discountValue!;
                        }
                    }
                    
                    const check_if_added = await tx.sales_items.where('sale_id').equals(selected_sale?.id!).and(x => x.item_id === item.items[0].masterId).toArray();
                    
                    if (check_if_added.length > 0) {
                        await tx.sales_items.update(check_if_added[0].id, { quantity_added: check_if_added[0].quantity_added + 1 });
                    } else {
                        await tx.sales_items.add({
                            sale_id: selected_sale?.id!,
                            item_id: item.items[0].masterId,
                            unit: item.items[0].unit,
                            unit_type: item.items[0].unitType,
                            barcode: item.items[0].barcode,
                            trade_name: item.items[0].tradeName,
                            quantity_added: 1,
                            item_discount: item_discount,
                            type: selectedValue.type === ItemType.MEDICAL.toString() ? "MEDICAL" : "NON_MEDICAL"
                        });
                        
                        for (let i = 0; i < item.items.length; i++) {
                            await tx.sales_items_batches.add({
                                sale_id: selected_sale?.id!,
                                item_id: item.items[i].masterId,
                                inventory_id: item.items[i].inventoryId,
                                batch_number: item.items[i].batchNumber,
                                expiry_date: item.items[i].isValidExpiryDate ? item.items[i].expiryDate?.toDate().toISOString() : undefined,
                                cost: item.items[i].costFromSupplier,
                                price: item.items[i].sellingPrice,
                                available_stock: item.items[i].availableQuantity,
                                sold_quantity: item.items[i].inputQuantity?.original! + item.items[i].inputQuantity?.bonus! - item.items[i].availableQuantity,
                                tax_profile: item.items[i].taxProfile?.toJsonString() || "{}",
                                type: selectedValue.type === ItemType.MEDICAL.toString() ? "MEDICAL" : "NON_MEDICAL",
                                is_selected: i === 0,
                                max_discount_type: item.items[i].metadata?.discountType.toString(),
                                max_discount_amount: item.items[i].metadata?.discountValue,
                                offers: ""
                            });
                        }
                    }
                });
                
                setTimeout(() => {
                    setValue(undefined);
                    setSearch('');
                    setResults([]);
                    setResetTrigger(prev => prev + 1);
                }, 0);
                
            } catch (e) {
                console.error("Error adding item:", e);
                toast({
                    title: "Error",
                    variant: "destructive",
                    description: "Failed to add item to sale",
                    duration: 5000,
                });
            }
        };
        
        getMed();
    }, [value, selected_sale?.id]);

    const TableCellStyled = ({ className = "py-0 font-medium", ...props }) => (
        <TableCell className={cn(tableCellClass, className)} {...props} />
    );
    const TableHeadStyled = ({ className = "border", ...props }) => (
        <TableHead className={cn(tableHeadClass, className)} {...props} />
    );

    return (
        <>
            <StartShiftModal
                value={openShiftStart}
                setValue={setOpenShiftStart}
                setDisplaySalesComp={setDisplaySalesComp}
                showTriggerButton={false}
                page='sales'
            />
            {
                !displaySalesComp
                    ?
                    <NoShiftFound
                        value={openShiftStart}
                        setValue={setOpenShiftStart}
                        setDisplaySalesComp={setDisplaySalesComp}
                    />
                    :
                    <div
                        className='h-full flex'
                    >
                        <div className='flex flex-col w-full'>
                            <div className={``}>
                                <div className='flex items-center justify-between border-b-[0.5px] pb-4 mb-4'>
                                    <div className='flex items-center justify-center space-x-6'>
                                        {active_sales?.map((s, i) => (
                                            <div
                                                key={i}
                                                onClick={async () => {
                                                    try {
                                                        await db.active_sales.bulkUpdate(active_sales.map(a => {
                                                            return {
                                                                key: a.id,
                                                                changes: {
                                                                    selected: a.id === s.id
                                                                }
                                                            }
                                                        }))
                                                    } catch (e) {
                                                        console.error(e)
                                                    }
                                                }}
                                            >
                                                <ContextMenu>
                                                    <ContextMenuTrigger>
                                                        <p className={`text-sm font-semibold cursor-pointer ${selected_sale?.id === s.id ? "bg-gray-100" : ""
                                                            } px-3 py-2 rounded-lg`}>Sale {i + 1}</p>
                                                    </ContextMenuTrigger>
                                                    <ContextMenuContent>
                                                        <ContextMenuItem onClick={async () => {
                                                            try {
                                                                await db.active_sales.bulkUpdate(active_sales.map(a => {
                                                                    return {
                                                                        key: a.id,
                                                                        changes: {
                                                                            selected: a.id === s.id
                                                                        }
                                                                    }
                                                                }))
                                                            } catch (e) {
                                                                console.error(e)
                                                            }
                                                        }}>Go to Sale</ContextMenuItem>
                                                        <ContextMenuItem onClick={async () => {
                                                            try {
                                                                await db.transaction('rw', db.active_sales, db.sales_items, db.sales_items_batches, async (tx) => {
                                                                    const to_beDeleted_sale = await tx.active_sales.where('id').equals(s.id).toArray();
                                                                    const to_beDeletedSaleItems = await tx.sales_items.where('sale_id').equals(s.id).toArray();
                                                                    const to_beDeletedSaleItemsBatches = await tx.sales_items_batches.where('sale_id').equals(s.id).toArray();
                                                                    await tx.active_sales.bulkDelete(to_beDeleted_sale.map(a => a.id));
                                                                    await tx.sales_items.bulkDelete(to_beDeletedSaleItems.map(a => a.id));
                                                                    await tx.sales_items_batches.bulkDelete(to_beDeletedSaleItemsBatches.map(a => a.id));
                                                                    active_sales && (await tx.active_sales.update(active_sales[0].id, { selected: true }));
                                                                })
                                                            } catch (e) {
                                                                console.error(e)
                                                            }
                                                        }} disabled={
                                                            active_sales?.length === 1
                                                        }>Discard Sale</ContextMenuItem>
                                                    </ContextMenuContent>
                                                </ContextMenu>
                                            </div>
                                        ))}
                                        <Button
                                            variant="outline"
                                            className='text-sm'
                                            onClick={addNewMultiSale}
                                        >
                                            <p>+ Add Sale</p>
                                        </Button>
                                    </div>
                                    {/* <div className="flex items-center justify-between border rounded-md space-x-4 px-4 py-3 min-w-[200px]">
                                        <p className='text-sm font-semibold'>Insurance Sale</p>
                                        <Switch 
                                            className='w-10 h-4'
                                            checked={switchInsurance}
                                            onCheckedChange={() => {setSwitchInsurance(!switchInsurance)}}
                                        />
                                    </div> */}
                                </div>

                                {active_sales?.map(a => a.id).includes(selected_sale?.id!) ? <div className='flex items-center justify-center space-x-12 mb-8'>
                                    <p className='text-3xl font-semibold'>Product list</p>
                                    <div className='flex-1 mx-4 relative'>
                                        <div className='border rounded-md px-2 flex items-center'>
                                        <AutoComplete
                                            options={results}
                                            emptyMessage="No results."
                                            placeholder="Search for a product"
                                            onValueChange={setValue}
                                            value={value}
                                            input={search}
                                            onInputChanged={setSearch}
                                            shouldFilter={false}
                                            ref={autoCompleteRef}
                                            key={`search-${selected_sale?.id}-${resetTrigger}`}
                                        />
                                            <Button
                                                className='px-2 py-0 h-8'
                                                variant={'outline'}
                                                size={'sm'}
                                                onClick={() => {
                                                    setSearch('');
                                                    setValue(undefined)
                                                    setResults([])
                                                    setResetTrigger(prev => prev + 1);
                                                }}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                    </div>

                                    <Button
                                        onClick={() => {
                                            setSelected([])
                                            try {
                                                db.transaction('rw', db.sales_items, db.sales_items_batches, async (tx) => {
                                                    const to_beDeletedSaleItems = await tx.sales_items.where('sale_id').equals(selected_sale?.id!).toArray();
                                                    const to_beDeletedSaleItemsBatches = await tx.sales_items_batches.where('sale_id').equals(selected_sale?.id!).toArray();
                                                    await tx.sales_items.bulkDelete(to_beDeletedSaleItems.map(a => a.id));
                                                    await tx.sales_items_batches.bulkDelete(to_beDeletedSaleItemsBatches.map(a => a.id));
                                                })
                                            } catch (e) {
                                                console.error(e)
                                            }
                                        }}
                                    >
                                        Remove All
                                    </Button>
                                </div> : <NoSaleSelected />}
                            </div>
                            {active_sales?.map(a => a.id).includes(selected_sale?.id!) && <div className='flex-1 overflow-auto pb-4'>
                                <div className={`h-full relative flex flex-col`}>
                                    <Table className='mb-24'>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHeadStyled className="border border-l">Product Name</TableHeadStyled>
                                                <TableHeadStyled className="border">Batch No. | Barcode Number | Expiry Date</TableHeadStyled>
                                                <TableHeadStyled className="border">Available Stock</TableHeadStyled>
                                                <TableHeadStyled className="border">Unit Type</TableHeadStyled>
                                                <TableHeadStyled className="border">Quantity</TableHeadStyled>
                                                <TableHeadStyled className="border">Discount</TableHeadStyled>
                                                <TableHeadStyled className="border">Total</TableHeadStyled>
                                                <TableHeadStyled className="border"></TableHeadStyled>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {
                                                selected_items?.map && selected_items.filter(a => a.sale_id === selected_sale?.id).map((item, index: number) => {
                                                    const price = selected_items_batches?.find((batch) => batch.item_id === item.item_id && batch.is_selected && batch.sale_id === item.sale_id)?.price!;
                                                    const tax_profile = selected_items_batches?.find((batch) => batch.item_id === item.item_id && batch.is_selected && batch.sale_id === item.sale_id)?.tax_profile;
                                                    const available_stock = selected_items_batches?.find((batch) => batch.item_id === item.item_id && batch.is_selected && batch.sale_id === item.sale_id)?.available_stock!;
                                                    const inventory_id = selected_items_batches?.find((batch) => batch.item_id === item.item_id && batch.is_selected && batch.sale_id === item.sale_id)?.inventory_id;
                                                    const discount_type = selected_items_batches?.find((batch) => batch.item_id === item.item_id && batch.is_selected && batch.sale_id === item.sale_id)?.max_discount_type;
                                                    const offers = selected_items_batches?.find((batch) => batch.item_id === item.item_id && batch.is_selected && batch.sale_id === item.sale_id)?.offers ? JSON.parse(selected_items_batches?.find((batch) => batch.item_id === item.item_id && batch.is_selected && batch.sale_id === item.sale_id)?.offers!) as Offer[] : [] as Offer[];
                                                    return (
                                                        <TableRow
                                                            key={index}
                                                            className={index === selectedItemIndex ? 'bg-gray-100' : ''}>
                                                            <TableCellStyled>{item.trade_name}</TableCellStyled>
                                                            <TableCellStyled>
                                                                <Select
                                                                    value={inventory_id}
                                                                    onValueChange={(value) => {
                                                                        try {
                                                                            db.transaction('rw', db.sales_items, db.sales_items_batches, async (tx) => {
                                                                                await tx.sales_items_batches.where('sale_id').equals(selected_sale?.id!).and(itemC => itemC.item_id === item.item_id).modify({ is_selected: false });
                                                                                await tx.sales_items_batches.where('sale_id').equals(selected_sale?.id!).and(itemC => itemC.item_id === item.item_id).and(itemC => itemC.inventory_id === value).modify({ is_selected: true });
                                                                                await tx.sales_items.where('sale_id').equals(selected_sale?.id!).and(itemC => itemC.item_id === item.item_id).modify({ quantity_added: 1 });
                                                                                const discount_type_target = selected_items_batches?.find((batch) => batch.inventory_id === value && batch.item_id === item.item_id && batch.sale_id === item.sale_id)?.max_discount_type;
                                                                                if (discount_type_target === "ALWAYS") {
                                                                                    await tx.sales_items.where('sale_id').equals(selected_sale?.id!).and(item => item.item_id === item.item_id).modify({ item_discount: selected_items_batches?.find((batch) => batch.inventory_id === value && batch.item_id === item.item_id && batch.sale_id === item.sale_id)?.max_discount_amount });
                                                                                } else {
                                                                                    await tx.sales_items.where('sale_id').equals(selected_sale?.id!).and(item => item.item_id === item.item_id).modify({ item_discount: 0 });
                                                                                }
                                                                            })
                                                                        } catch (e) {
                                                                            console.error(e)
                                                                        }
                                                                    }}
                                                                >
                                                                    <SelectTrigger className="w-[280px] h-8">
                                                                        <SelectValue placeholder="Select a batch" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectGroup>
                                                                            {
                                                                                selected_items_batches?.filter(a => {
                                                                                    return a.item_id === item.item_id && a.sale_id === selected_sale?.id;
                                                                                }).map((batch, index) => (
                                                                                    <SelectItem key={index} value={batch.inventory_id} disabled={batch.available_stock <= 0}>
                                                                                        {batch.batch_number} {item.barcode ? "| " + item.barcode : ""} | {dayjs(batch.expiry_date).isValid() ? dayjs(batch.expiry_date).format('DD/MM/YYYY') : "N/A"}
                                                                                    </SelectItem>
                                                                                ))
                                                                            }
                                                                        </SelectGroup>
                                                                    </SelectContent>
                                                                </Select>

                                                            </TableCellStyled>
                                                            <TableCellStyled>{available_stock}</TableCellStyled>
                                                            <TableCellStyled>{item.unit_type}</TableCellStyled>
                                                            <TableCellStyled className="">
                                                                <QuantityModal
                                                                    item={item}
                                                                    available_stock={selected_items_batches?.find((batch) => batch.item_id === item.item_id && batch.is_selected && batch.sale_id === item.sale_id)?.available_stock!}
                                                                    selected_sale={selected_sale?.id!}
                                                                />
                                                            </TableCellStyled>
                                                            <TableCellStyled>
                                                                <DiscountModal
                                                                    item_discount={item.item_discount}
                                                                    trade_name={item.trade_name}
                                                                    allowed_to_open={discount_type !== DiscountType.NONE.toString() || offers?.length! > 0}
                                                                    discount_type={discount_type!}
                                                                    max_discount_value={selected_items_batches?.find((batch) => batch.item_id === item.item_id && batch.is_selected && batch.sale_id === item.sale_id)?.max_discount_amount!}
                                                                    price={price}
                                                                    sale_id={selected_sale?.id!}
                                                                    item_id={item.item_id}
                                                                    quantity={item.quantity_added}
                                                                    offers={offers || []}
                                                                />
                                                            </TableCellStyled>
                                                            <TableCellStyled>
                                                                {
                                                                    convertToCurrencyRounded(getItemCost({
                                                                        price: price!,
                                                                        quantity: item.quantity_added!,
                                                                        taxProfile: tax_profile!,
                                                                        discount: item.item_discount!,
                                                                        discount_type: discount_type!
                                                                    }).cost)
                                                                }
                                                            </TableCellStyled>
                                                            <TableCellStyled><DeleteIcon onClick={async () => {
                                                                try {
                                                                    await db.transaction('rw', db.sales_items, db.sales_items_batches, async (tx) => {
                                                                        const to_beDeletedSaleItems = await tx.sales_items.where('sale_id').equals(selected_sale?.id!).and(x => x.item_id === item.item_id).toArray();
                                                                        const to_beDeletedSaleItemsBatches = await tx.sales_items_batches.where('sale_id').equals(selected_sale?.id!).and(x => x.item_id === item.item_id).toArray();
                                                                        await tx.sales_items.bulkDelete(to_beDeletedSaleItems.map(a => a.id));
                                                                        await tx.sales_items_batches.bulkDelete(to_beDeletedSaleItemsBatches.map(a => a.id));
                                                                    })
                                                                } catch (e) {
                                                                    console.error(e)
                                                                }
                                                            }} style={{
                                                                cursor: 'pointer'
                                                            }} /></TableCellStyled>
                                                        </TableRow>
                                                    )
                                                }
                                                )}
                                        </TableBody>
                                    </Table>
                                    <div className='w-full absolute bottom-0'>
                                        <Footer
                                            internalSalesId={selected_sale?.id!}
                                            total={
                                                netTotal
                                            }
                                            discount={
                                                totaldiscount
                                            }
                                            totalTax={
                                                totaltax
                                            }
                                            applicableTaxes={
                                                applicableTaxRates
                                            }
                                            checkoutButtonRef={checkoutButtonRef}
                                        />
                                    </div>
                                </div>
                            </div>}
                        </div>
                        {
                            switchInsurance ? (
                                <div className='bg-[#F9FAFB] flex-1 px-4 mx-2 min-w-[400px] relative'>
                                    <div className="grid w-full max-w-sm items-center gap-1.5 mt-4">
                                        <Label
                                            htmlFor="emirates"
                                            aria-required="true"
                                        >Emirates ID <span className='text-red-500'>*</span></Label>
                                        <Input
                                            type="text"
                                            id="emirates"
                                            placeholder="***-****-*******-*"
                                            value={emiratesID}
                                            onChange={(event) => {
                                                let newValue = event.target.value.replace(/\D/g, ''); // Remove non-digits

                                                // Insert dashes at the correct positions
                                                if (newValue.length > 3) {
                                                    newValue = newValue.slice(0, 3) + '-' + newValue.slice(3);
                                                }
                                                if (newValue.length > 8) {
                                                    newValue = newValue.slice(0, 8) + '-' + newValue.slice(8);
                                                }
                                                if (newValue.length > 16) {
                                                    newValue = newValue.slice(0, 16) + '-' + newValue.slice(16);
                                                }
                                                setEmiratesID(newValue);
                                            }}
                                            maxLength={18}
                                            required
                                        />
                                    </div>

                                    <div className="grid w-full max-w-sm items-center gap-1.5 mt-4">
                                        <Label
                                            htmlFor="erx"
                                            aria-required="true"
                                        >ERX <span className='text-red-500'>*</span></Label>
                                        <Input
                                            type="text"
                                            id="erx"
                                            placeholder="Need to check"
                                            // value={erx}
                                            // onChange={(event) => {
                                            //     let newValue = event.target.value.replace(/\D/g, ''); // Remove non-digits

                                            //     // Insert dashes at the correct positions
                                            //     if (newValue.length > 3) {
                                            //     newValue = newValue.slice(0, 3) + '-' + newValue.slice(3);
                                            //     }
                                            //     if (newValue.length > 8) {
                                            //     newValue = newValue.slice(0, 8) + '-' + newValue.slice(8);
                                            //     }
                                            //     if (newValue.length > 16) {
                                            //     newValue = newValue.slice(0, 16) + '-' + newValue.slice(16);
                                            //     }
                                            //     setERX(newValue);
                                            // }}
                                            required
                                        />
                                    </div>

                                    <hr
                                        className='my-4'
                                    />

                                    <Button
                                        className='w-full'
                                    >
                                        <CheckCircle
                                            className='mr-2 h-4 w-4'
                                        /> Submit & Retrieve items
                                    </Button>

                                    <Button
                                        className='w-full mt-4 text-gray-400'
                                        variant={'outline'}
                                    >
                                        <CheckCircle
                                            className='mr-2 h-4 w-4 text-gray-400'
                                        /> Print Prescription
                                    </Button>

                                    <div
                                        className='absolute bottom-2 bg-[#EF4444] p-4 rounded-lg'
                                    >
                                        <p className='text-[#FAFAFA] font-semibold'>ERX not found!</p>
                                        <p className='text-[#FAFAFA] text-sm'>Please check the prescription and try again.</p>
                                        <Button
                                            variant={'outline'}
                                            className='text-white bg-[#EF4444] mt-4'
                                        >Try Again</Button>
                                    </div>
                                </div>
                            ) : null
                        }
                    </div>
            }
        </>
    );
}

export default component